import { Component, ChangeDetectionStrategy, HostListener, inject } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { injectContext } from '@taiga-ui/polymorpheus';
import { TuiDialogContext } from '@taiga-ui/core';
import { isEqual } from 'lodash-es';
import cloneDeep from 'lodash/cloneDeep';
import { TranslateService } from '@ngx-translate/core';
import { BaseResizableComponent } from '@src/app/modules/base';
import { AlertService, PreferencesService } from '@src/core/services';
import { Nullable, Optional } from '@src/types/utils';

import { LoyaltyProgramApiService } from '../services';
import { LoyaltyProgramProduct, LoyaltyProgramProductInput } from '../models';
import { INIT_PRODUCT } from '../constants';

import { ProductDialogStep } from './types';

@Component({
  selector: 'lp-product-dialog',
  templateUrl: './product-dialog.component.html',
  styleUrls: ['./product-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductDialogComponent extends BaseResizableComponent {
  private readonly alertService = inject(AlertService);
  private readonly loyaltyProgramApiService = inject(LoyaltyProgramApiService);
  private readonly translateService = inject(TranslateService);
  private readonly context = injectContext<TuiDialogContext<void | string, Optional<LoyaltyProgramProduct>>>();
  private readonly preferencesService = inject(PreferencesService);

  /** Текущий шаг формы */
  step: Nullable<ProductDialogStep> = ProductDialogStep.Common;

  /** Ссылка на форму текущего шага */
  stepForm?: UntypedFormGroup;

  /** Продукт */
  product: LoyaltyProgramProductInput = INIT_PRODUCT;
  product$ = new BehaviorSubject<Optional<LoyaltyProgramProductInput>>(undefined);

  loading = false;

  steps = Object.values(ProductDialogStep);

  /** Параметр что текущая ассоциация является РЕКЛАМОДАТЕЛЕМ */
  isAdvertiser = false;

  constructor() {
    super();

    this.product = cloneDeep(this.context.data ?? INIT_PRODUCT);
    this.product$.pipe(takeUntil(this.destroyed$$)).subscribe(entity => {
      if (entity) {
        this.product = entity;
      }
    });

    this.preferencesService.settings$.pipe(takeUntil(this.destroyed$$)).subscribe(settings => {
      this.isAdvertiser = !!settings?.isAdvertiserAssociation;
      this.cdr.markForCheck();
    });
  }

  get title() {
    if (this.context.data?.id) {
      return this.translateService.instant('components.productDialog.labels.edit');
    }

    return this.translateService.instant('components.productDialog.labels.add');
  }

  get applyTitle() {
    if (!this.isLastStep) {
      return this.translateService.instant('common.buttons.next');
    }

    return this.context.data?.id
      ? this.translateService.instant('common.buttons.save')
      : this.translateService.instant('common.buttons.add');
  }

  get productId() {
    return this.context.data?.id;
  }

  get canMoveBack(): boolean {
    return this.getStepIndex() > 0;
  }

  get isLastStep() {
    return this.getStepIndex() === this.steps.length - 1;
  }

  back() {
    this.step = this.steps[this.getStepIndex() - 1];
  }

  close() {
    this.context.completeWith();
  }

  apply() {
    if (!this.isFormValid()) {
      return;
    }

    const stepIndex = this.getStepIndex();
    if (stepIndex !== this.steps.length - 1) {
      this.step = this.steps[stepIndex + 1];
      this.cdr.markForCheck();

      return;
    }

    if (isEqual(this.product, this.context.data)) {
      this.close();

      return;
    }

    this.loading = true;

    this.save().finally(() => {
      this.loading = false;
      this.cdr.markForCheck();
    });
  }

  @HostListener('window:keydown', ['$event'])
  keyEvent(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.context.completeWith();
    }
  }

  private async save() {
    if (!this.product) {
      return;
    }

    try {
      if (this.context.data?.id) {
        // обновляем продукт
        this.context.completeWith(await this.loyaltyProgramApiService.updateProduct(this.product, this.context.data));
      } else {
        // создаем новый
        this.context.completeWith(await this.loyaltyProgramApiService.createProduct(this.product));
      }
    } catch (err) {} // Вывод ошибки реализован в сервисе
  }

  private getStepIndex() {
    return this.steps.findIndex(item => item === this.step);
  }

  private isFormValid() {
    if (this.stepForm) {
      this.stepForm.markAllAsTouched();

      if (this.stepForm.invalid) {
        this.alertService.error(this.translateService.instant('common.alerts.errors.fillAndCheckFields'));

        return false;
      }
    }

    return true;
  }
}
